import { useNavigate } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";
import { ConfirmationModal } from "@/components/confirmation-modal";
import { Link } from "@/components/link";
import { toast } from "@/components/toast/toast-queue";
import { FeaturePageLayout } from "@/features/shared/layout/FeaturePageLayout";
import { dateFormatter } from "@/utils/dateFormatter";

import { CheckForm } from "./CheckForm";
import { useChecks } from "./hooks/useChecks";
import { useDeleteCheck } from "./hooks/useDeleteCheck";

interface CheckPageProps {
  readonly checkId?: string;
}

export function CheckPage({ checkId }: CheckPageProps) {
  const navigate = useNavigate();
  const checks = useChecks();
  const deleteCheck = useDeleteCheck();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const check = checks.data?.find((item) => item._id === checkId) ?? null;
  const isLoading = checks.isLoading;
  const isMissing = checkId !== undefined && !isLoading && !check;

  const goToChecks = () =>
    navigate({ search: { tab: "checks" }, to: "/admin/mbc" });

  const handleDelete = () => {
    if (!checkId) return;

    deleteCheck.mutate(checkId, {
      onError: (error) => {
        toast.add({
          description: error.message,
          title: "Could not delete the check",
          type: "error",
        });
      },
      onSuccess: () => {
        setIsDeleteModalOpen(false);
        goToChecks();
      },
    });
  };

  return (
    <div className="p-xl">
      <FeaturePageLayout
        backButton
        headerContent={
          check && (
            <Button
              className="ml-auto"
              color="destructive"
              isDisabled={check.usedBy.length > 0}
              onPress={() => setIsDeleteModalOpen(true)}
              size="lg"
              variant="tertiary"
            >
              Delete
            </Button>
          )
        }
        isLoading={isLoading}
        title={checkId ? "Edit check" : "New check"}
      >
        {isMissing ? (
          <p className="p-6 text-sm text-text-tertiary">Check not found</p>
        ) : (
          <CheckForm
            check={check}
            key={checkId}
            onCancel={goToChecks}
            onSaved={goToChecks}
            sidebar={
              check && (
                <aside className="flex flex-col gap-4 rounded-xl border border-border-secondary p-5">
                  <h2 className="text-md font-semibold text-text-primary">
                    Used by
                  </h2>
                  {check.usedBy.length > 0 ? (
                    <>
                      <ul className="flex flex-col gap-2 text-sm">
                        {check.usedBy.map((guideline) => (
                          <li key={guideline._id}>
                            <Link
                              className="text-sm"
                              href={`/admin/mbc/guidelines/${guideline._id}`}
                            >
                              {guideline.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <p className="text-sm text-text-tertiary">
                        Remove the check from these guidelines to delete it.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-text-tertiary">
                      Not in any guideline
                    </p>
                  )}
                  <p className="border-t border-border-secondary pt-4 text-sm text-text-tertiary">
                    Last updated {dateFormatter(check.updatedAt)}
                  </p>
                </aside>
              )
            }
          />
        )}
      </FeaturePageLayout>

      <ConfirmationModal
        confirmButtonText={deleteCheck.isPending ? "Deleting..." : "Delete"}
        icon={<Trash2 className="text-featured-icon-light-fg-error" />}
        isConfirmLoading={deleteCheck.isPending}
        isOpen={isDeleteModalOpen}
        onConfirm={handleDelete}
        onOpenChange={setIsDeleteModalOpen}
        text={`"${check?.label}" will be removed from the checks library.`}
        title={`Delete check ${check?.label}?`}
      />
    </div>
  );
}
